import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { postsDebug } from './postUtils';

export default function PostingProgressDialog({ open, onClose, platforms, status }) {

  postsDebug('PostingProgressDialog render:', { open, platforms, status });
  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook':
        return '📘';
      case 'linkedin':
        return '💼';
      case 'tumblr':
        return '🎨';
      case 'youtube':
        return '📺';
      case 'instagram':
        return '📷';
      case 'pinterest':
        return '📌';
      default:
        return '🌐';
    }
  };

  const getStatusIcon = (platformStatus) => {
    if (platformStatus === 'success') return '✅';
    if (platformStatus === 'error') return '❌';
    if (platformStatus === 'pending') return '⏳';
    return '⏳';
  };

  const getStatusColor = (platformStatus) => {
    if (platformStatus === 'success') return '#4caf50';
    if (platformStatus === 'error') return '#f44336';
    if (platformStatus === 'pending') return '#ff9800';
    return '#9e9e9e';
  };

  const completedCount = Object.values(status).filter(
    (s) => s === 'success',
  ).length;
  const totalCount = platforms.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={24} />
          <Typography variant="h6">Publishing to Social Media</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Progress: {completedCount}/{totalCount} platforms
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({Math.round(progress)}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {platforms.map((platform) => (
            <Card
              key={platform}
              sx={{
                border: `2px solid ${getStatusColor(
                  status[platform] || 'pending',
                )}`,
                bgcolor:
                  status[platform] === 'success'
                    ? 'rgba(76, 175, 80, 0.1)'
                    : status[platform] === 'error'
                      ? 'rgba(244, 67, 54, 0.1)'
                      : 'rgba(255, 152, 0, 0.1)',
              }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                    {getPlatformIcon(platform)}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {platform.charAt(0).toUpperCase() + platform.slice(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {status[platform] === 'success'
                        ? 'Posted successfully!'
                        : status[platform] === 'error'
                          ? 'Failed to post'
                          : 'Publishing...'}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                    {getStatusIcon(status[platform] || 'pending')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          disabled={completedCount < totalCount}
        >
          {completedCount === totalCount ? 'Done' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );

}
